import { Box, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import React, { useMemo } from "react";
import { useData } from "@pure-ptr/react";
import { HoverColumn, pageStore, renderHoverLink, renderProcessTypeHoverLink, useGlobalStore, XDataGrid } from "../../common";
import { BooleanEquators, integerEquators, StringEquators } from "../../store/constants/pages";
import { createClaimNumberFieldWithCopyButton, createCurrencyDataField, createNumberDataField, createPlainDateField, createStringDataField, createSecondLevelField } from "../../util/format";
import PageviewIcon from '@mui/icons-material/Pageview';
import { useDispatch } from "react-redux";
import { setAdjustment } from "../../store/actions/claimAnalysis";

export const ChargeAnalysisGrid = ({ paginationFilters, selectedClaimsIdsPtr, loading, data,showSearchFilters,
    onLoad, refresh,defaultCustomFilter, setClaimId
}) => {

    const dispatch = useDispatch();
    const fields = useColumnDefinitions();

    const handleViewServiceAdjustment = (rowData) => {
        setClaimId(rowData?.claimAnalysisId);
        dispatch(setAdjustment({
            open: true,
            type: 'SERVICE',
            id: rowData?.servicepaymentid
        }));
    }

    const actions = params => [
        <GridActionsCellItem
            onClick={() => handleViewServiceAdjustment( params.row )}
            label='View Service Adjustments'
            icon={
                <Tooltip title="View Service Adjustments">
                    <PageviewIcon />
                </Tooltip>
            }
        />
    ];

    return <>
        <XDataGrid
            fields={fields}
            actions={actions}

            data={data}
            loading={loading}

            selectedIdsPtr={selectedClaimsIdsPtr}
            showSearchFilters={showSearchFilters}
            paginationFilters={ paginationFilters }

            onLoad={onLoad}
            refresh={refresh}
            defaultCustomFilter={defaultCustomFilter}

            name="charge-analysis-landing"
            defaultSort={DEFAULT_SORT}
            defaultHiddenFields={DEFAULT_HIDDEN_FIELDS}
            criteriasSaveEnabled={false}
        />
    </>
}

const useColumnDefinitions = () => {
    const tinyContracts = useGlobalStore( x => x.contracts.tiny ?? [] );
    const facilities = useGlobalStore( x => x.facilities.data ?? [] );
    return useMemo(() => {
        const fields = [
            createClaimNumberFieldWithCopyButton('claimnumber', 'Claim Number', { returnWholeRow: true, sortable: true, equators: StringEquators, positionFixed: true, reorder: false, customCell: true, hideable: false }),
            createStringDataField('contractName', 'Contract Name', { 
                sortable: true, 
                equators: StringEquators, 
                onHover: true, 
                isDropdown: true, 
                dropdownValues:tinyContracts?.map((item)=>item?.name) ?? [],
                width: '100px',

                renderCell: renderHoverLink(
                    row => [
                        {
                            label: "Name",
                            data: row?.contractName,
                        }, {
                            type: "LINK",
                            label: "Go to Contracts",
                            data: `/cms/contract/view/${row?.contractId}`,
                        },
                    ]
                )
            }),
            createStringDataField('payerName', 'Payer Name', { sortable: true, equators: StringEquators, width: '100px' }),
            createSecondLevelField('rule', 'Rule Name', 'name', { 
                onHover: true,
                equators: StringEquators,
                hideFromFilter: true,
                renderCell: renderProcessTypeHoverLink( row => {
                    return [
                    {
                        label: "Name",
                        data: row?.rule?.name || ''
                    }, {
                        label: "Type",
                        data: row?.rule?.ruleTypeName || ''
                    }, {
                        label: "Pay Method",
                        data: row?.rule?.rulePayMethod || ''
                    }, {
                        label: "Pay Value",
                        data: row?.rule?.rulePayValue || ''
                    },
                    {
                        type: "LINK",
                        label: "Go to Rule",
                        data: `/cms/contract/${row?.contractId}/rule/${row?.contractServiceId}/edit/${row?.ruleId}/root/true`,
                    }
                ]

            })
            }),
            createStringDataField('revCode', 'Revenue Code', { 
                sortable: true, 
                onHover: true,
                equators: StringEquators,
                renderCell : renderHoverLink( row => [{
                        label: "",
                        data: row.revCodeDescription || ''
                    }]
                )
            }),
            createStringDataField('pxCode', 'Procedure Code', { 
                sortable: true, 
                onHover: true,
                equators: StringEquators,
                renderCell : renderHoverLink( row => {
                    let pxCodeColumns: HoverColumn[] = [
                        {
                            label: "",
                            data: row.pxCodeDescription || ''
                        }
                    ];
            
                    if (row?.feeScheduleId && row.pxCode) {
                        if (row.pxCode.toString().includes(':')) {
                            const splitedPxCode = row.pxCode?.split(":");
                            pxCodeColumns.push({
                                type: "LINK",
                                label: "Go to Fee Schedule Item",
                                data: `/cms/feeschedules/view/${row.feeScheduleId}/${splitedPxCode[0]}`,
                            })
                        } else {
                            pxCodeColumns.push({
                                type: "LINK",
                                label: "Go to Fee Schedule Item",
                                data: `/cms/feeschedules/view/${row.feeScheduleId}/${row.pxCode}`,
                            })
                        }
                    }
        
                    
                    return pxCodeColumns;
                })
            }),
            // createNumberDataField('units', 'Qty/Units', { sortable: true }),
            
            createCurrencyDataField('chargedAmount', 'Charged Amount', { sortable: true, equators: integerEquators }),
            createCurrencyDataField('expectedPay', 'Expected Pay', { sortable: true, equators: integerEquators }),
            createCurrencyDataField('actualPay', 'Allowable', { sortable: true, equators: integerEquators }),
            createCurrencyDataField('variance', 'Variance', { sortable: true, equators: integerEquators, labelStyle: 'label label-lg label-inline' }),
            createStringDataField('facilityId', 'Facility', { equators: BooleanEquators, hidden: true, isStrictDropdown:true, dropdownValues: facilities?.map((item)=>({label:item?.name,value:item?.id,name:item?.name})) }),
            createStringDataField('rule.name', 'Rule Name', { equators: StringEquators, hidden: true, secondLevel: 'name'  }),
        ];
        return fields;
    }, [facilities, tinyContracts]);
}

const DEFAULT_SORT = [{ id: 'claimnumber', direction: 'desc' }];
const DEFAULT_HIDDEN_FIELDS = [];
const { usePageStore } = pageStore('charge-analysis-landing');